import { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  FileText,
  Filter,
  RefreshCw,
  ArrowLeft,
  Check,
  X,
  Edit3,
  Save,
  AlertCircle,
  BarChart3
} from 'lucide-react';
import { evaluationApi, EvalQuestion, EvalStats, EvalQuestionUpdate } from '../../services/evaluationApi';

interface QuestionDetailProps {
  question: EvalQuestion;
  onApprove: () => void;
  onReject: (notes?: string) => void;
  onUpdate: (updates: EvalQuestionUpdate) => void;
  onClose: () => void;
}

function QuestionDetail({ question, onApprove, onReject, onUpdate, onClose }: QuestionDetailProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedQuestion, setEditedQuestion] = useState(question.question);
  const [editedExpectedAnswer, setEditedExpectedAnswer] = useState(question.expected_answer || '');
  const [rejectNotes, setRejectNotes] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  const handleSave = () => {
    onUpdate({
      question: editedQuestion,
      expected_answer: editedExpectedAnswer,
    });
    setIsEditing(false);
  };

  const handleReject = () => {
    onReject(rejectNotes || undefined);
    setShowRejectModal(false);
    setRejectNotes('');
  };

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-200 rounded transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <h2 className="text-lg font-semibold">Question #{question.id}</h2>
          {question.is_approved && (
            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
              Approved
            </span>
          )}
          {question.is_reviewed && !question.is_approved && (
            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded">
              Rejected
            </span>
          )}
          {!question.is_reviewed && (
            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
              Pending Review
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded transition-colors"
            >
              <Edit3 size={16} />
              Edit
            </button>
          ) : (
            <button
              onClick={handleSave}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-500 text-white hover:bg-blue-600 rounded transition-colors"
            >
              <Save size={16} />
              Save
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Question */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Question</label>
          {isEditing ? (
            <textarea
              value={editedQuestion}
              onChange={(e) => setEditedQuestion(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={3}
            />
          ) : (
            <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{question.question}</p>
          )}
        </div>

        {/* Expected Answer */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Expected Answer</label>
          {isEditing ? (
            <textarea
              value={editedExpectedAnswer}
              onChange={(e) => setEditedExpectedAnswer(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={4}
              placeholder="Enter the expected answer..."
            />
          ) : (
            <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">
              {question.expected_answer || <span className="text-gray-400 italic">No expected answer provided</span>}
            </p>
          )}
        </div>

        {/* Metadata Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Type</p>
            <p className="text-sm font-medium text-gray-900">{question.question_type || 'N/A'}</p>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Difficulty</p>
            <p className="text-sm font-medium text-gray-900">{question.difficulty || 'N/A'}</p>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Module</p>
            <p className="text-sm font-medium text-gray-900">{question.expected_module || 'N/A'}</p>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Generated By</p>
            <p className="text-sm font-medium text-gray-900">{question.generated_by || 'N/A'}</p>
          </div>
        </div>

        {/* Source Documents */}
        {question.source_doc_titles && question.source_doc_titles.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Source Documents</label>
            <div className="flex flex-wrap gap-2">
              {question.source_doc_titles.map((title, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full"
                >
                  <FileText size={14} />
                  {title}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Source Path */}
        {question.source_doc_path && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Source Path</label>
            <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded font-mono break-all">
              {question.source_doc_path}
            </p>
          </div>
        )}

        {/* Evaluation Results (if evaluated) */}
        {question.last_eval_at && (
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-sm font-medium text-gray-700 mb-4">Last Evaluation Results</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Answer Quality</p>
                <p className={`text-sm font-medium ${
                  question.answer_quality === 'correct' ? 'text-green-600' :
                  question.answer_quality === 'partial' ? 'text-yellow-600' :
                  question.answer_quality === 'wrong' ? 'text-red-600' :
                  'text-gray-600'
                }`}>
                  {question.answer_quality || 'N/A'}
                </p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Retrieval Hit</p>
                <p className={`text-sm font-medium ${question.retrieval_hit ? 'text-green-600' : 'text-red-600'}`}>
                  {question.retrieval_hit ? 'Yes' : 'No'}
                </p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Confidence</p>
                <p className="text-sm font-medium text-gray-900">
                  {question.last_eval_confidence !== null
                    ? `${(question.last_eval_confidence * 100).toFixed(1)}%`
                    : 'N/A'}
                </p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Evaluated At</p>
                <p className="text-sm font-medium text-gray-900">
                  {new Date(question.last_eval_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            {question.last_eval_response && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Last Response</label>
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg max-h-48 overflow-y-auto">
                  {question.last_eval_response}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Reviewer Notes */}
        {question.reviewer_notes && (
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <p className="text-sm font-medium text-yellow-800 mb-1">Reviewer Notes</p>
            <p className="text-sm text-yellow-700">{question.reviewer_notes}</p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {!question.is_reviewed && (
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={() => setShowRejectModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
          >
            <X size={18} />
            Reject
          </button>
          <button
            onClick={onApprove}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            <Check size={18} />
            Approve
          </button>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Reject Question</h3>
            <textarea
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              placeholder="Optional: Add notes explaining why this question is being rejected..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              rows={4}
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function EvaluationReview() {
  const [questions, setQuestions] = useState<EvalQuestion[]>([]);
  const [stats, setStats] = useState<EvalStats | null>(null);
  const [selectedQuestion, setSelectedQuestion] = useState<EvalQuestion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [filterType, setFilterType] = useState<string>('');
  const [filterModule, setFilterModule] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const pageSize = 20;

  const loadQuestions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, unknown> = {
        limit: pageSize,
        offset: currentPage * pageSize,
      };
      if (filterType) params.types = filterType;
      if (filterModule) params.modules = filterModule;
      if (filterStatus === 'approved') params.approved_only = true;
      if (filterStatus === 'pending' || filterStatus === 'rejected') params.reviewed_only = filterStatus === 'rejected';

      const data = await evaluationApi.getQuestions(params as Parameters<typeof evaluationApi.getQuestions>[0]);

      // Filter by status locally if needed
      let filtered = data;
      if (filterStatus === 'pending') {
        filtered = data.filter(q => !q.is_reviewed);
      } else if (filterStatus === 'rejected') {
        filtered = data.filter(q => q.is_reviewed && !q.is_approved);
      }

      setQuestions(filtered);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load questions');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterType, filterModule, filterStatus]);

  const loadStats = useCallback(async () => {
    try {
      const data = await evaluationApi.getStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  useEffect(() => {
    loadQuestions();
    loadStats();
  }, [loadQuestions, loadStats]);

  const handleApprove = async (questionId: number) => {
    try {
      await evaluationApi.approveQuestion(questionId);
      await loadQuestions();
      await loadStats();
      if (selectedQuestion?.id === questionId) {
        setSelectedQuestion(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve question');
    }
  };

  const handleReject = async (questionId: number, notes?: string) => {
    try {
      await evaluationApi.rejectQuestion(questionId, notes);
      await loadQuestions();
      await loadStats();
      if (selectedQuestion?.id === questionId) {
        setSelectedQuestion(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject question');
    }
  };

  const handleUpdate = async (questionId: number, updates: EvalQuestionUpdate) => {
    try {
      const updated = await evaluationApi.updateQuestion(questionId, updates);
      setQuestions(prev => prev.map(q => q.id === questionId ? updated : q));
      if (selectedQuestion?.id === questionId) {
        setSelectedQuestion(updated);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update question');
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;
    try {
      await evaluationApi.bulkApprove(Array.from(selectedIds));
      setSelectedIds(new Set());
      await loadQuestions();
      await loadStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to bulk approve');
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === questions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(questions.map(q => q.id)));
    }
  };

  // Get unique types and modules for filters
  const types = stats ? Object.keys(stats.by_type).filter(t => t !== 'null' && t !== 'unknown') : [];
  const modules = stats ? Object.keys(stats.by_module).filter(m => m !== 'null' && m !== 'unknown') : [];

  if (selectedQuestion) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="max-w-4xl mx-auto">
          <QuestionDetail
            question={selectedQuestion}
            onApprove={() => handleApprove(selectedQuestion.id)}
            onReject={(notes) => handleReject(selectedQuestion.id, notes)}
            onUpdate={(updates) => handleUpdate(selectedQuestion.id, updates)}
            onClose={() => setSelectedQuestion(null)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Evaluation Question Review</h1>
              <p className="text-sm text-gray-500 mt-1">Review and approve generated evaluation questions</p>
            </div>
            <a
              href="/"
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={18} />
              Back to Chat
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <BarChart3 size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                  <p className="text-sm text-gray-500">Total Questions</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <AlertCircle size={20} className="text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total - stats.reviewed}</p>
                  <p className="text-sm text-gray-500">Pending Review</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.approved}</p>
                  <p className="text-sm text-gray-500">Approved</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <CheckCircle size={20} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.evaluated}</p>
                  <p className="text-sm text-gray-500">Evaluated</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters and Actions */}
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter size={18} className="text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Filters:</span>
            </div>

            <select
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value as typeof filterStatus); setCurrentPage(0); }}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>

            <select
              value={filterType}
              onChange={(e) => { setFilterType(e.target.value); setCurrentPage(0); }}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              {types.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>

            <select
              value={filterModule}
              onChange={(e) => { setFilterModule(e.target.value); setCurrentPage(0); }}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Modules</option>
              {modules.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>

            <div className="flex-1" />

            <button
              onClick={() => loadQuestions()}
              className="flex items-center gap-2 px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <RefreshCw size={16} />
              Refresh
            </button>

            {selectedIds.size > 0 && (
              <button
                onClick={handleBulkApprove}
                className="flex items-center gap-2 px-4 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                <CheckCircle size={16} />
                Approve Selected ({selectedIds.size})
              </button>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Questions Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw size={32} className="mx-auto text-gray-400 animate-spin" />
              <p className="text-gray-500 mt-2">Loading questions...</p>
            </div>
          ) : questions.length === 0 ? (
            <div className="p-8 text-center">
              <FileText size={48} className="mx-auto text-gray-300" />
              <p className="text-gray-500 mt-2">No questions found</p>
              <p className="text-sm text-gray-400">Try adjusting your filters or generate new questions</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === questions.length && questions.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Question</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Module</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Source Document</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {questions.map((q) => (
                  <tr key={q.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(q.id)}
                        onChange={() => toggleSelect(q.id)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">#{q.id}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setSelectedQuestion(q)}
                        className="text-sm text-gray-900 hover:text-blue-600 text-left max-w-md truncate block"
                      >
                        {q.question}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                        {q.question_type || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{q.expected_module || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                      {q.source_doc_titles?.[0] || 'N/A'}
                    </td>
                    <td className="px-4 py-3">
                      {q.is_approved ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                          <CheckCircle size={12} />
                          Approved
                        </span>
                      ) : q.is_reviewed ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs rounded">
                          <XCircle size={12} />
                          Rejected
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded">
                          <AlertCircle size={12} />
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedQuestion(q)}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          title="View details"
                        >
                          <Edit3 size={16} />
                        </button>
                        {!q.is_reviewed && (
                          <>
                            <button
                              onClick={() => handleApprove(q.id)}
                              className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                              title="Approve"
                            >
                              <Check size={16} />
                            </button>
                            <button
                              onClick={() => handleReject(q.id)}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                              title="Reject"
                            >
                              <X size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Pagination */}
          {questions.length > 0 && (
            <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing {currentPage * pageSize + 1} - {currentPage * pageSize + questions.length}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                  disabled={currentPage === 0}
                  className="p-2 text-gray-600 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={18} />
                </button>
                <span className="text-sm text-gray-600">Page {currentPage + 1}</span>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={questions.length < pageSize}
                  className="p-2 text-gray-600 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
